import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { createAdminProduct } from "../services/productsApi";
import { useAuth } from "../auth/AuthContext";
import { Product } from "../types/product";

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProductCreated: (product: Product) => void;
  initialBarcode?: string;
}

const toNumber = (...values: (string | number | undefined | null)[]): number | null => {
  for (const val of values) {
    if (val === null || val === undefined) continue;
    const num = typeof val === "string" ? parseFloat(val) : Number(val);
    if (!Number.isNaN(num)) return num;
  }
  return null;
};

export const AddProductModal: React.FC<AddProductModalProps> = ({
  isOpen,
  onClose,
  onProductCreated,
  initialBarcode = "",
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    barcode: initialBarcode,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: "",
        price: "",
        barcode: initialBarcode,
      });
      setError(null);
    }
  }, [isOpen, initialBarcode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }
    if (!formData.price.trim()) {
      setError("Price is required");
      return;
    }
    const priceNum = parseFloat(formData.price);
    if (Number.isNaN(priceNum) || priceNum <= 0) {
      setError("Price must be a valid positive number");
      return;
    }
    if (!formData.barcode.trim()) {
      setError("Barcode is required");
      return;
    }

    setLoading(true);

    try {
      const companyId =
        toNumber(user?.company_id, user?.company?.id, user?.companyId) ?? 1;
      const locationId =
        toNumber(
          user?.location_id,
          user?.location?.id,
          user?.locationId,
          user?.default_location_id
        ) ?? 1;

      const product = await createAdminProduct({
        company_id: companyId,
        location_id: locationId,
        name: formData.name.trim(),
        price: priceNum,
        barcode: formData.barcode.trim(),
      });

      onProductCreated(product);
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to create product. Please try again."
      );
      console.error("Error creating product:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[1100]">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#402B69]/60 hover:text-[#885ABB] transition"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <h2 className="text-xl font-semibold text-[#885ABB] mb-4 pb-2 border-b border-[#885ABB]">
          Add Product
        </h2>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-[#402B69] mb-1">
              Enter Name:
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full border border-[#E5E0EB] rounded-md px-3 py-2 text-[#402B69] focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
              placeholder="Product name"
              disabled={loading}
            />
          </div>

          {/* Price Field */}
          <div>
            <label className="block text-sm font-medium text-[#402B69] mb-1">
              Enter Price:
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              step="0.01"
              min="0"
              className="w-full border border-[#E5E0EB] rounded-md px-3 py-2 text-[#402B69] focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
              placeholder="0.00"
              disabled={loading}
            />
          </div>

          {/* Barcode Field */}
          <div>
            <label className="block text-sm font-medium text-[#402B69] mb-1">
              Enter Barcode:
            </label>
            <input
              type="text"
              name="barcode"
              value={formData.barcode}
              onChange={handleChange}
              className="w-full border border-[#E5E0EB] rounded-md px-3 py-2 text-[#402B69] focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
              placeholder="Barcode"
              disabled={loading}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#885ABB] hover:bg-[#6f3fa8] text-white rounded-md py-2 px-4 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
};
